import { sortBasedOnDate, sortBasedOnSeverity } from 'helpers/alerts'
import { color } from 'styles/colors'
import _ from 'underscore'

const ALERT_HEIGHT = 25
const ALERT_WIDTH = 25
const Y_OFFSET_PADDING = 0
const Y_OFFSET_PADDING_FULLSCREEN_RTH = -10
const Y_OFFSET_PADDING_MAINTENANCE = 27

const addOrUpdateAlerts = (alertGroupClassName, layout, chartDivId, alerts, selectedLegendAlerts, deviceConnectionStatusAlerts, tooltipId = 'alert-tooltip') => {
  const bottomPlot = AlertPlotBandBuilder.getBottomPlotDetails(chartDivId, layout)
  const alertsPlaceHolderName = `${chartDivId}_${alertGroupClassName}`
  const alertsPlaceholder = createOrGetExistingAlertsPlaceholder(bottomPlot, alertsPlaceHolderName, chartDivId)

  if (!alertsPlaceholder) {
    return
  }

  removeExistingAlertsChildren(alertsPlaceholder) // removes exiting or old alerts

  const alertsWithTransform = []
  const deviceConnectionStatusAlertsWithTransform = []
  const selectedAlertCategories = _.filter(alerts, (alert) => selectedLegendAlerts?.includes(alert.severity)) // filter alerts based on legend selection

  _.forEach(selectedAlertCategories, alert => {
    const xPosition = getTheXTransformValue(alert.occurredAt, bottomPlot.xaxisTimeMin, bottomPlot.xaxisTimeMax, bottomPlot.xMin, bottomPlot.xMax)
    if (xPosition || xPosition === 0) { // if alert in visible range
      alertsWithTransform.push({ ...alert, x: xPosition })
    }
  })

  const selectedDeviceConnectionStatusAlerts = _.filter(deviceConnectionStatusAlerts, (alert) => selectedLegendAlerts?.includes(alert.state)) // filter device connection status alerts based on legend selection
  _.forEach(selectedDeviceConnectionStatusAlerts, (alert) => {
    const xPosition = getTheXTransformValue(alert.occurredAt, bottomPlot.xaxisTimeMin, bottomPlot.xaxisTimeMax, bottomPlot.xMin, bottomPlot.xMax)

    if (xPosition) { // if alert in visible range
      deviceConnectionStatusAlertsWithTransform.push({ ...alert, x: xPosition })
    }
  })

  // if device connection status alert is in visible range
  const alertGroups = buildAlertGroups(alertsWithTransform, deviceConnectionStatusAlertsWithTransform)
  createAlertIcons(alertGroups, alertsPlaceholder, tooltipId)
}

const buildAlertGroups = (alertsWithTransformPosition, deviceConnectionStatusAlertsWithTransform) => {
  const alertGroups = []
  // group device status alerts with other alerts
  const alertsWithTransform = [...alertsWithTransformPosition, ...deviceConnectionStatusAlertsWithTransform]
  sortBasedOnDate(alertsWithTransform, 'occurredAt') // sort alerts in descending order based on occurredAt
  alertsWithTransform.reverse() // convert to ascending order

  alertsWithTransform.forEach(alertMap => {
    if (!alertGroups.length) {
      alertGroups.push([alertMap])
    } else {
      const lastGroup = _.last(alertGroups)

      sortBasedOnSeverity(lastGroup)

      const previousAlert = lastGroup[0] // get highest severity alert icon position

      if ((alertMap.x - previousAlert.x) >= ALERT_WIDTH) { // if alert not overlapping with previous one
        alertGroups.push([alertMap]) // add as new group
      } else {
        lastGroup.push(alertMap) // add to overlapped alert group
      }
    }
  })

  return alertGroups
}

const removeExistingAlertsChildren = (element) => {
  const childNodes = [...(element?.children ?? [])]

  _.each(childNodes, (child) => {
    if (child) {
      element.removeChild(child)
      removeExistingAlertsChildren(child)
    }
  })
}

const getTheXTransformValue = (time, xaxisTimeMin, xaxisTimeMax, minX, maxX) => {
  const minTime = new Date(xaxisTimeMin)
  const maxTime = new Date(xaxisTimeMax)
  const alertTime = new Date(time)

  if (minTime <= alertTime && alertTime <= maxTime) { // if with in the visible range of time range
    const timeDiff = (maxTime.getTime() - minTime.getTime()) * 10000
    const perOne = (maxX - minX) / timeDiff
    const current = (alertTime.getTime() - minTime.getTime()) * 10000

    const eachValue = (perOne * current)
    return eachValue
  }

  return null
}

const createOrGetExistingAlertsPlaceholder = (bottomPlot, name, chartDivId) => {
  const alertsPlaceHolder = document.getElementsByClassName(name)

  if (!alertsPlaceHolder.length) {
    const chart = document.getElementById(chartDivId)
    if (!chart) {
      return null
    }
    const chartSvg = chart.getElementsByClassName('layer-below')

    if (!chartSvg.length || !bottomPlot) {
      return null
    }

    const placeholder = document.createElementNS('http://www.w3.org/2000/svg', 'g')
    placeholder.classList.add(name)
    chartSvg[0].parentElement.appendChild(placeholder)
    transformAlertsPlaceholder(placeholder, bottomPlot) // transform alerts placeholder
    return placeholder
  }

  transformAlertsPlaceholder(alertsPlaceHolder[0], bottomPlot) // transform alerts placeholder
  return alertsPlaceHolder[0]
}

const transformAlertsPlaceholder = (alertsPlaceHolder, bottomPlot) => {
  alertsPlaceHolder.setAttribute('transform', `translate(${bottomPlot.xMin - ALERT_WIDTH * 0.5}, ${bottomPlot.yOffset - ALERT_HEIGHT})`)
}

const getBottomPlotDetails = (chartDivId, layout) => {
  const chart = document.getElementById(chartDivId)

  // Return default values if chart is not found or not properly initialized
  if (!chart || !chart._fullLayout || !chart._fullLayout.xaxis) {
    return {
      xMin: 0,
      xMax: 100,
      yOffset: 0,
      xaxisTimeMin: layout?.xaxis?.range?.[0] || 0,
      xaxisTimeMax: layout?.xaxis?.range?.[1] || 100
    }
  }

  const width = chart._fullLayout.xaxis._length

  // this methods gets shared between rth and maintence trends charts
  // so we need to check if the chart is rth chart or not
  // if it is rth chart then we need to get the yaxis3 offset
  const rthCharts = ['plotly-rth-chart', 'plotly-serverless-rth-chart', 'full-plotly']
  const xOffset = rthCharts.includes(chartDivId) ? chart._fullLayout.xaxis._offset : chart._fullLayout.xaxis._offset - 5
  const yOffsetRthPadding = chartDivId === 'full-plotly' ? Y_OFFSET_PADDING_FULLSCREEN_RTH : Y_OFFSET_PADDING

  // Add null checks for yaxis access
  const yaxis3 = chart._fullLayout.yaxis3
  const yaxis = chart._fullLayout.yaxis
  const yOffset = rthCharts.includes(chartDivId) && yaxis3
    ? yaxis3._offset + yOffsetRthPadding
    : (yaxis ? yaxis._offset + Y_OFFSET_PADDING_MAINTENANCE : 0)

  const xaxisTimeMin = layout?.xaxis?.range?.[0] || 0
  const xaxisTimeMax = layout?.xaxis?.range?.[1] || 100

  return { xMin: xOffset, xMax: xOffset + width, yOffset, xaxisTimeMin, xaxisTimeMax }
}

const createAlertIcons = (alertGroups, alertsPlaceHolder, tooltipId = 'alert-tooltip') => {
  alertGroups.forEach(alertGroup => {
    sortBasedOnSeverity(alertGroup)

    const alert = alertGroup[0]

    const alertIds = _.map(sortBasedOnSeverity(alertGroup), r => r.id).join('-')
    const gElement = document.createElementNS('http://www.w3.org/2000/svg', 'g')
    gElement.setAttribute('data-tooltip-id', tooltipId)
    gElement.setAttribute('data-tooltip-content', alertIds)
    gElement.style.pointerEvents = 'all'
    gElement.style.zIndex = 5
    gElement.style.outline = 'none'
    gElement.style.userSelect = 'none'

    const alertIcon = alert.status ? alert.status : 'new'

    const image = document.createElementNS('http://www.w3.org/2000/svg', 'image')
    image.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    image.setAttributeNS(null, 'height', ALERT_HEIGHT)
    image.setAttributeNS(null, 'width', ALERT_WIDTH)

    if (alert.state && (alert.state === 'connected' || alert.state === 'disconnected' || alert.state === 'restarted')) {
      image.setAttributeNS(null, 'href', '/img/alert-rth-spirte-icon/device_status_info.svg')
    } else {
      image.setAttributeNS(null, 'href', `/img/alert-rth-spirte-icon/${alert.severity}-${alertIcon}.svg`)
    }
    image.setAttributeNS(null, 'x', '0')
    image.setAttributeNS(null, 'y', '0')
    gElement.appendChild(image)

    image.setAttribute('x', alert.x) // transform

    if (alertGroup.length > 1) { // if group has more than one alert
      const gTextElement = document.createElementNS('http://www.w3.org/2000/svg', 'g')
      gTextElement.setAttribute('transform', `translate(${alert.x + (ALERT_WIDTH * 0.8)}, 10)`)
      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
      circle.setAttributeNS(null, 'r', 7)
      circle.setAttribute('fill', color('rthAlertGroupBadgeBackground'))
      circle.style.pointerEvents = 'all'

      gTextElement.appendChild(circle)

      const textNode = document.createTextNode(alertGroup.length)
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text')
      text.setAttribute('fill', color('paneBackground'))
      text.style.fontSize = '9px'
      text.style.fontWeight = 'bold'
      text.style.pointerEvents = 'all'
      text.style.textAnchor = 'middle'
      text.style.alignmentBaseline = 'central'
      text.style.zIndex = 5
      text.appendChild(textNode)

      gTextElement.appendChild(text)
      gElement.appendChild(gTextElement)
    }

    alertsPlaceHolder.appendChild(gElement)
  })
}

const AlertPlotBandBuilder = {
  addOrUpdateAlerts,
  getBottomPlotDetails
}

export default AlertPlotBandBuilder
